import type { User } from '@gmn-portal/backend';
import { redirect } from 'react-router-dom';
import type { CrudAction, ServiceKey, UserGroupDomain } from '@/shared/utils/permissions';
import {
  canReadService,
  resolveUserGroup,
  resolveUserGroupDomain,
  resolveUserGroupKey,
} from '@/shared/utils/permissions';

import { getDefaultRouteForUser } from './navigation-routes';

const getRedirectParam = (request?: Request) => {
  if (!request) return null;
  const url = new URL(request.url);
  const redirectParam = url.searchParams.get('redirect') || url.searchParams.get('redirectPath');
  if (!redirectParam || !redirectParam.startsWith('/')) return null;
  return redirectParam;
};

const redirectToLogin = (request?: Request) => {
  if (!request) {
    return redirect('/auth/login');
  }
  const url = new URL(request.url);
  const redirectPath = `${url.pathname}${url.search}${url.hash}`;
  return redirect(`/auth/login?redirect=${encodeURIComponent(redirectPath)}`);
};

export const redirectBasedOnRole = (user: User) => {
  return redirect(getDefaultRouteForUser(user));
};

export const publicRoute = (user: User | null, request?: Request) => {
  if (!user) return null;
  const redirectParam = getRedirectParam(request);
  return redirectParam ? redirect(redirectParam) : redirectBasedOnRole(user);
};

type PrivateRouteOptions = {
  domain?: UserGroupDomain;
  service?: ServiceKey;
  action?: CrudAction;
  allowedGroupKeys?: string[];
  /**
   * Group keys that bypass the service/action permission check (additive).
   * Used for roles with a conditional permission the flag-based check cannot
   * express (e.g. JUH dispatchers may create island/clinic/land/fuel-station objects despite
   * lacking a generic object create flag). The domain check still applies.
   */
  bypassServiceCheckForGroupKeys?: string[];
};

const hasServiceAccess = (
  user: User,
  service: ServiceKey,
  action: CrudAction = 'read'
): boolean => {
  const group = resolveUserGroup(user);
  if (!group) return false;

  if (action === 'read') {
    return canReadService(group, service);
  }

  const permission = group.permissions?.find((entry) => entry.service === service);
  return Boolean(permission?.[action]);
};

export const privateRoute =
  (options: PrivateRouteOptions) => (user: User | null, request?: Request) => {
    if (!user) {
      return redirectToLogin(request);
    }

    const group = resolveUserGroup(user);
    if (!group) {
      return redirectToLogin(request);
    }

    if (options.allowedGroupKeys?.length) {
      const key = resolveUserGroupKey(user);
      if (!key || !options.allowedGroupKeys.includes(key)) {
        return redirectBasedOnRole(user);
      }
    }

    if (options.domain) {
      const domain = resolveUserGroupDomain(user);
      if (!domain || domain !== options.domain) {
        return redirectBasedOnRole(user);
      }
    }

    if (options.service) {
      const key = resolveUserGroupKey(user);
      const bypassesServiceCheck = Boolean(
        key && options.bypassServiceCheckForGroupKeys?.includes(key)
      );
      if (
        !bypassesServiceCheck &&
        !hasServiceAccess(user, options.service, options.action ?? 'read')
      ) {
        return redirectBasedOnRole(user);
      }
    }

    return null;
  };

export const unknownRoute = (user: User | null, request?: Request) => {
  return user ? redirectBasedOnRole(user) : redirectToLogin(request);
};
